import {
  ChakraFlex,
  EquipUIFireflyV2Theme,
  FAIconName,
  FireflyButton,
  TertiaryTagDropdown,
  Text,
  useToastMessage,
} from '@equip.health/ui';
import { useEffect, useMemo, useState } from 'react';
import type { FlexProps } from '@chakra-ui/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { InterventionStatusDropdownOptions } from '~/lib/constants/carePlan.constants';
import { CarePlanInterventionStatus } from '~/lib/types/carePlan/CarePlanInterventionStatus';
import {
  CarePlanNoteResponse,
  CarePlanResponse,
} from '~/lib/types/carePlan/carePlans';
import { partitionProblemsForNote } from '~/lib/util/carePlan.util';
import { useEditNoteDrawerContext } from '~/lib/contexts/EditNoteDrawerContext';
import { useUpdateInterventionMutation } from '~/store/services/carePlan';
import { PatientNoteGoalCard } from '~/components/maud/patients/note/PatientNoteGoalCard';
import { PatientNoteCoOccurringGoalCard } from '~/components/maud/patients/note/PatientNoteCoOccurringGoalCard';
import { HiddenCoOccurringProblemsActionBar } from '~/components/maud/patients/note/HiddenCoOccurringProblemsActionBar';
import { useFilteredCarePlanNoteInterventions } from '~/lib/hooks/care-team/useFilteredCarePlanNoteInterventions';
import { AddCustomInterventionModal } from '~/components/maud/patients/profile/tabs/careplan/AddCustomInterventionModal';
import { PatientNoteQolGoalCard } from './PatientNoteQolGoalCard';
import { useLatestNoteTemplateRevisionsWithLegacyHandling } from '~/components/maud/patients/note/useLatestNoteTemplateRevisionsWithLegacyHandling';
import { useFormikContext } from 'formik';
import { EditNoteFormData } from '~/lib/types/notes/EditNoteFormData';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { SessionGuideEvents } from '~/analytics/sessionGuideEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { AnalyticsEvent } from '~/analytics';
import { CarePlanEvents } from '~/analytics/carePlanEvents';
import { InterventionText } from '../profile/tabs/careplan/InterventionText';
import { useGetInterventionCategoriesEnumQuery } from '~/store/services/refCarePlanEnums';

const { neutral } = EquipUIFireflyV2Theme.colors;

export type PatientNoteActiveCarePlanProps = {
  carePlanOnNote: CarePlanNoteResponse;
  activeCarePlan?: CarePlanResponse;
  treatmentEpisodeId: string;
  showFrequencyErrors: boolean;
};

const emptyStateChakraProps: FlexProps = {
  border: `1px dashed ${neutral.line}`,
  borderRadius: '12px',
  justifyContent: 'center',
  padding: '18px',
};

export const PatientNoteActiveCarePlan = ({
  carePlanOnNote,
  activeCarePlan,
  treatmentEpisodeId,
  showFrequencyErrors,
}: PatientNoteActiveCarePlanProps) => {
  const [updateIntervention, { isError: isUpdateInterventionError }] =
    useUpdateInterventionMutation();
  const { values: formValues } = useFormikContext<EditNoteFormData>();
  const { createToast } = useToastMessage();
  const { noteId: noteExternalId } = useEditNoteDrawerContext();
  const { trackEvent } = useAnalytics();

  const [isAddInterventionModalOpen, setIsAddInterventionModalOpen] =
    useState(false);

  const { data: categories } = useGetInterventionCategoriesEnumQuery();

  const {
    carePlanM4MedPsychNotes: isCarePlanM4MedPsychNotesEnabled,
    carePlanM4MedPsychProblems,
    carePlanM4CustomInterventions: isCustomInterventionsEnabled,
  } = useFlags<LDFlags>();

  const sendAnalyticsEvent = (analyticsEvent: AnalyticsEvent) => {
    trackEvent(analyticsEvent, {
      Source: AnalyticsSourceValues.SESSION_GUIDE,
    });
  };
  const { noteTemplateRevisions } =
    useLatestNoteTemplateRevisionsWithLegacyHandling(
      formValues.noteTemplateRevision?.node,
    );

  const selectedNoteTemplate = noteTemplateRevisions?.find(
    (rev) => rev.templateRevisionId === formValues.noteTemplateRevision?.node,
  )?.template;

  const isMedPsychTemplate =
    selectedNoteTemplate?.templateCode === 'FUP_MED_TEAM' ||
    selectedNoteTemplate?.templateCode === 'INTAKE_MED_TEAM';

  // Med/psych templates gate on the LD flag alone (DB flags not yet populated
  // for these templates); non-med templates keep the DB-flag rule.
  const shouldShowGoals = isMedPsychTemplate
    ? isCarePlanM4MedPsychNotesEnabled
    : !!selectedNoteTemplate?.showCarePlanGoals;

  const shouldShowInterventions = isMedPsychTemplate
    ? isCarePlanM4MedPsychNotesEnabled
    : !!selectedNoteTemplate?.showCarePlanInterventions;

  useEffect(() => {
    if (isUpdateInterventionError) {
      createToast({
        id: 'intervention-status-update-error',
        icon: FAIconName.exclamationTriangleRegular,
        text: "An error occurred updating the intervention's status. Please try again later.",
        variant: 'critical',
      });
    }
  }, [isUpdateInterventionError, createToast]);

  const { edProblems, coOccurringProblems, hiddenCoOccurringCount } = useMemo(
    () =>
      partitionProblemsForNote({
        problems: carePlanOnNote.problems,
        isMedPsychProblemsEnabled: carePlanM4MedPsychProblems,
        compare: (a, b) =>
          a.goals[0].pathwayGoal.sortOrder - b.goals[0].pathwayGoal.sortOrder,
        prefilter: (p) => p.goals.length > 0,
      }),
    [carePlanOnNote.problems, carePlanM4MedPsychProblems],
  );

  const sortedQolGoals = useMemo(() => {
    return carePlanOnNote.qolGoals.toSorted(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
  }, [carePlanOnNote.qolGoals]);

  const interventions = useFilteredCarePlanNoteInterventions(carePlanOnNote);

  return (
    <>
      {shouldShowGoals && (
        <>
          <Text variant="h5">Treatment goals & associated behaviors</Text>
          {isCarePlanM4MedPsychNotesEnabled ? (
            <>
              {edProblems.length === 0 &&
              sortedQolGoals.length === 0 &&
              coOccurringProblems.length === 0 ? (
                <ChakraFlex {...emptyStateChakraProps} alignItems="center">
                  <Text color="neutral.secondary">
                    You do not have any goals to review this session
                  </Text>
                </ChakraFlex>
              ) : (
                <>
                  {edProblems.length > 0 && (
                    <Text variant="body">
                      Review and update the status of each goal, and record how
                      often the patient engaged in each behavior{' '}
                      <span style={{ fontWeight: 500 }}>
                        over the past 7 days.
                      </span>
                    </Text>
                  )}

                  {edProblems.length > 0 && (
                    <ChakraFlex direction="column" gap="12px">
                      {edProblems.map((problem) => (
                        <PatientNoteGoalCard
                          key={problem.id}
                          noteExternalId={noteExternalId}
                          problem={problem}
                          showFrequencyErrors={showFrequencyErrors}
                          treatmentEpisodeId={treatmentEpisodeId}
                        />
                      ))}
                    </ChakraFlex>
                  )}
                </>
              )}
              {(coOccurringProblems.length > 0 ||
                hiddenCoOccurringCount > 0) && (
                <ChakraFlex direction="column" gap="12px" marginTop="18px">
                  <Text variant="h5">Co-occurring problems</Text>
                  {hiddenCoOccurringCount > 0 && (
                    <HiddenCoOccurringProblemsActionBar
                      count={hiddenCoOccurringCount}
                      id="patient-note-hidden-co-occurring-problems"
                    />
                  )}
                  <ChakraFlex direction="column" gap="12px">
                    {coOccurringProblems.map((problem) => (
                      <PatientNoteCoOccurringGoalCard
                        key={problem.id}
                        noteExternalId={noteExternalId}
                        problem={problem}
                        treatmentEpisodeId={treatmentEpisodeId}
                      />
                    ))}
                  </ChakraFlex>
                </ChakraFlex>
              )}
              {sortedQolGoals.length > 0 && (
                <ChakraFlex direction="column" gap="12px" marginTop="18px">
                  <Text variant="h5">Quality of life goals</Text>
                  <ChakraFlex direction="column" gap="12px">
                    {sortedQolGoals.map((qolGoal) => (
                      <PatientNoteQolGoalCard
                        key={qolGoal.id}
                        noteExternalId={noteExternalId}
                        qolGoal={qolGoal}
                        treatmentEpisodeId={treatmentEpisodeId}
                      />
                    ))}
                  </ChakraFlex>
                </ChakraFlex>
              )}
            </>
          ) : (
            <>
              {carePlanOnNote.problems.length === 0 &&
              carePlanOnNote.qolGoals.length === 0 ? (
                <ChakraFlex {...emptyStateChakraProps} alignItems="center">
                  <Text color="neutral.secondary">
                    You do not have any goals to review this session
                  </Text>
                </ChakraFlex>
              ) : (
                <>
                  {(edProblems.length > 0 || sortedQolGoals.length > 0) && (
                    <Text variant="body">
                      Review and update the status of each goal, and record how
                      often the patient engaged in each behavior{' '}
                      <span style={{ fontWeight: 500 }}>
                        over the past 7 days.
                      </span>
                    </Text>
                  )}

                  <ChakraFlex direction="column" gap="12px">
                    {edProblems.map((problem) => (
                      <PatientNoteGoalCard
                        key={problem.id}
                        noteExternalId={noteExternalId}
                        problem={problem}
                        showFrequencyErrors={showFrequencyErrors}
                        treatmentEpisodeId={treatmentEpisodeId}
                      />
                    ))}
                    {sortedQolGoals.map((qolGoal) => (
                      <PatientNoteQolGoalCard
                        key={qolGoal.id}
                        noteExternalId={noteExternalId}
                        qolGoal={qolGoal}
                        treatmentEpisodeId={treatmentEpisodeId}
                      />
                    ))}
                  </ChakraFlex>
                </>
              )}
            </>
          )}
        </>
      )}
      {shouldShowInterventions && (
        <>
          <ChakraFlex alignItems="center" justifyContent="space-between">
            <Text variant="h5">Interventions</Text>
            {isCustomInterventionsEnabled && activeCarePlan && (
              <FireflyButton
                id="patient-note-add-interventions-btn"
                leftIcon={FAIconName.plusRegular}
                onClick={() => {
                  sendAnalyticsEvent(
                    CarePlanEvents.clickOnAddInterventionsButton,
                  );
                  setIsAddInterventionModalOpen(true);
                }}
                size="small"
                variant="secondary"
              >
                Add interventions
              </FireflyButton>
            )}
          </ChakraFlex>
          {interventions.length === 0 ? (
            <ChakraFlex {...emptyStateChakraProps} alignItems="center">
              <Text color="neutral.secondary">
                You do not have any interventions to review this session
              </Text>
            </ChakraFlex>
          ) : (
            <ChakraFlex
              {...emptyStateChakraProps}
              border={`1px solid ${neutral.tertiary}`}
              flexDirection="column"
              justifyContent="center"
              alignItems="flex-start"
              gap="8px"
            >
              {interventions.map((intervention) => (
                <ChakraFlex
                  key={intervention.id}
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                  data-testid={`patient-note-drawer__intervention-row__${intervention.id}`}
                >
                  <InterventionText
                    categories={categories}
                    data-testid={`patient-note-drawer__intervention-name__${intervention.id}`}
                    intervention={intervention}
                    minWidth={0}
                  />
                  <TertiaryTagDropdown
                    menuPlacement="bottom"
                    onDropdownOpen={() => {
                      sendAnalyticsEvent(
                        SessionGuideEvents.sessionGuideInterventionStatusDropdownClicked,
                      );
                    }}
                    onOptionClick={(newStatus) => {
                      sendAnalyticsEvent(
                        SessionGuideEvents.sessionGuideInterventionStatusOptionSelected,
                      );
                      updateIntervention({
                        body: {
                          ...(noteExternalId ? { noteExternalId } : {}),
                          statusCode: newStatus as CarePlanInterventionStatus,
                        },
                        interventionId: intervention.id,
                        treatmentEpisodeId,
                      });
                    }}
                    options={InterventionStatusDropdownOptions}
                    value={intervention.status.code}
                  />
                </ChakraFlex>
              ))}
            </ChakraFlex>
          )}
        </>
      )}
      {isAddInterventionModalOpen && activeCarePlan && (
        <AddCustomInterventionModal
          carePlan={activeCarePlan}
          noteExternalId={noteExternalId}
          onClose={() => setIsAddInterventionModalOpen(false)}
          treatmentEpisodeId={treatmentEpisodeId}
        />
      )}
    </>
  );
};
